import { useEffect, type RefObject } from 'react';

/**
 * A canvas opening or closing beside the chat changes the conversation's width.
 * Off-screen messages then re-measure and the browser clamps the scroll offset
 * (often to the top). Put the reader back where they were once the width has
 * settled; a reader at the bottom is left to the thread's own follow behavior.
 */
export function useKeepScrollOnResize(viewport: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = viewport.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let width = element.clientWidth;
    let top = element.scrollTop;
    let bottomGap = element.scrollHeight - element.clientHeight - element.scrollTop;
    let frame = 0;
    let settling = false;
    const onScroll = () => {
      // A scroll event caused by the width change itself is the clamp, not the reader.
      if (settling || element.clientWidth !== width) return;
      top = element.scrollTop;
      bottomGap = element.scrollHeight - element.clientHeight - element.scrollTop;
    };
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      if (top <= 0 || bottomGap <= 8) return;
      const wanted = top;
      // Scroll events while the width settles are the browser clamping, not the reader.
      settling = true;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          element.scrollTo({ top: wanted, behavior: 'instant' });
          settling = false;
        });
      });
    });
    element.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [viewport]);
}
